import * as React from "react";
import { ScrollArea, ScrollBar } from "@/core/components/ui/scroll-area";
import { cn } from "@/core/utils/utils";

// Default width of the right-hand sidebar. The layout also exposes the resolved
// width as a CSS variable for descendants that need to reserve matching space.
const SIMPLE_SIDEBAR_DEFAULT_WIDTH = "436px";

type SimpleSidebarContext = {
  open: boolean;
  setOpen: (open: boolean) => void;
  width: string;
};

const SimpleSidebarContext = React.createContext<SimpleSidebarContext | null>(
  null,
);

const useSimpleSidebar = () => {
  const context = React.useContext(SimpleSidebarContext);
  if (!context) {
    throw new Error(
      "useSimpleSidebar must be used within a SimpleSidebarProvider",
    );
  }
  return context;
};

interface SimpleSidebarLayoutProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  sidebarWidth?: string;
  className?: string;
}

const SimpleSidebarLayout = React.forwardRef<
  HTMLDivElement,
  SimpleSidebarLayoutProps
>(
  (
    {
      children,
      open: controlledOpen,
      onOpenChange,
      defaultOpen = false,
      sidebarWidth = SIMPLE_SIDEBAR_DEFAULT_WIDTH,
      className,
      ...props
    },
    ref,
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

    const open = controlledOpen ?? uncontrolledOpen;
    const setOpen = React.useCallback(
      (value: boolean) => {
        onOpenChange?.(value);
        setUncontrolledOpen(value);
      },
      [onOpenChange],
    );

    React.useEffect(() => {
      if (!open) return;
      document.body.dataset.pylonOffset = "true";
      document.body.style.setProperty("--pylon-sidebar-width", sidebarWidth);
      return () => {
        delete document.body.dataset.pylonOffset;
        document.body.style.removeProperty("--pylon-sidebar-width");
      };
    }, [open, sidebarWidth]);

    const value = React.useMemo(
      () => ({
        open,
        setOpen,
        width: sidebarWidth,
      }),
      [open, setOpen, sidebarWidth],
    );

    return (
      <SimpleSidebarContext.Provider value={value}>
        <div
          ref={ref}
          className={cn("relative h-[calc(100vh-66px)] w-full", className)}
          style={
            {
              "--simple-sidebar-width": sidebarWidth,
            } as React.CSSProperties
          }
          {...props}
        >
          {children}
        </div>
      </SimpleSidebarContext.Provider>
    );
  },
);
SimpleSidebarLayout.displayName = "SimpleSidebarLayout";

const SimpleSidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & {
    scrollAreaClassName?: string;
    removeScrollArea?: boolean;
  }
>(
  (
    { className, children, scrollAreaClassName, removeScrollArea, ...props },
    ref,
  ) => {
    const { open, width } = useSimpleSidebar();

    return (
      <div
        ref={ref}
        className={cn(
          "h-full transition-[width] duration-300 ease-in-out",
          className,
        )}
        style={{
          width: open ? `calc(100% - ${width})` : "100%",
        }}
        {...props}
      >
        {removeScrollArea ? (
          children
        ) : (
          <ScrollArea className={cn("h-full w-full", scrollAreaClassName)}>
            {children}
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </div>
    );
  },
);
SimpleSidebarContent.displayName = "SimpleSidebarContent";

const SimpleSidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, children, ...props }, ref) => {
  const { open, width } = useSimpleSidebar();

  return (
    <div
      ref={ref}
      data-state={open ? "open" : "closed"}
      className={cn(
        "fixed right-0 top-[64px] h-[calc(100vh-64px)] overflow-hidden border-l bg-card",
        "shadow-2xl",
        "z-20",
        "transition-transform duration-300 ease-in-out",
        "data-[state=closed]:translate-x-full",
        "flex flex-col",
        className,
      )}
      style={{ width }}
      {...props}
    >
      {children}
    </div>
  );
});
SimpleSidebar.displayName = "SimpleSidebar";

export {
  SimpleSidebarLayout,
  SimpleSidebarContent,
  SimpleSidebar,
  useSimpleSidebar,
};
